/* eslint-disable */
import React, { FC, useState, useMemo, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { useRecoilValue } from 'recoil';
import { activitiesState, langState, categorySetState } from '../../state/activities';
import { getActivities } from '../../utils/categoryConfig';
import * as XLSX from 'xlsx';
import {
  calcTotal,
  calcTotalPerc,
  calcTotalTime,
  calcTimePercentage,
  calcTimeMin,
  calcTimeAvarage,
  calcNumberOfDays,
  calcDaysPercentage,
  calcTotalTimeCategory,
  calTotalNumberOfActivites,
  getMins,
  calcMins,
} from '../../utils/statisticsCounter';

type Dimension = 'activities' | 'places' | 'togetherness' | 'mood';

const DIMENSION_LABELS: Record<Dimension, Record<string, string>> = {
  activities: { SV: 'Aktiviteter', EN: 'Activities' },
  places: { SV: 'Platser', EN: 'Places' },
  togetherness: { SV: 'Tillsammans', EN: 'Togetherness' },
  mood: { SV: 'Sinnesstämning', EN: 'Mood' },
};

const DIMENSION_COLORS: Record<Dimension, string> = {
  activities: '#1e3a8a',
  places: '#047857',
  togetherness: '#9333ea',
  mood: '#d97706',
};

/** Parse "HH:MM" to total minutes */
const parseMin = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

/** Build duration-based rows for non-activity dimensions */
function buildDimensionRows(
  data: any[],
  dimension: Dimension,
  lang: string,
) {
  const valueKey = dimension === 'places' ? 'where'
    : dimension === 'togetherness' ? 'with'
    : dimension === 'mood' ? 'feeling'
    : 'level';

  // Gather all entries across all days with durations
  const entryMap = new Map<string, { totalMins: number; count: number; days: Set<number> }>();

  data.forEach((day: any, dayIdx: number) => {
    const arr = day[dimension];
    if (!arr || arr.length === 0) return;
    for (let i = 0; i < arr.length; i++) {
      const entry = arr[i];
      const startMin = parseMin(entry.start);
      const nextStart = i + 1 < arr.length ? parseMin(arr[i + 1].start) : 1440;
      const duration = Math.max(0, nextStart - startMin);
      const label = String(entry[valueKey]);

      if (!entryMap.has(label)) {
        entryMap.set(label, { totalMins: 0, count: 0, days: new Set() });
      }
      const rec = entryMap.get(label)!;
      rec.totalMins += duration;
      rec.count += 1;
      rec.days.add(dayIdx);
    }
  });

  const totalMinsAll = Array.from(entryMap.values()).reduce((s, r) => s + r.totalMins, 0) || 1;
  const totalCountAll = Array.from(entryMap.values()).reduce((s, r) => s + r.count, 0) || 1;
  const totalDays = data.length || 1;

  return Array.from(entryMap.entries()).map(([label, rec]) => ({
    name: label,
    count: rec.count,
    countPct: ((rec.count / totalCountAll) * 100).toFixed(1) + '%',
    totalMins: rec.totalMins,
    timePct: ((rec.totalMins / totalMinsAll) * 100).toFixed(1) + '%',
    avgMins: rec.count > 0 ? Math.round(rec.totalMins / rec.count) : 0,
    days: rec.days.size,
    daysPct: ((rec.days.size / totalDays) * 100).toFixed(1) + '%',
  }));
}

const HVBStatistics: FC = () => {
  const activities = useRecoilValue(activitiesState);
  const lang = useRecoilValue(langState) as 'SV' | 'EN';
  const categorySet = useRecoilValue(categorySetState);
  const act: Array<any> = activities as Array<any>;
  const taxonomyObjects = getActivities(categorySet);

  const [dimension, setDimension] = useState<Dimension>('activities');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');

  // Filter by date
  const filteredAct = useMemo(() => {
    let source = act;
    if (selectedDate !== 'all') {
      const idx = parseInt(selectedDate);
      source = [act[idx]];
    }
    return source.map((day) => ({
      ...day,
      activities: (day.activities || []).filter(
        (a: any) => a.category !== 'Inget Loggat' && a.activity !== 'Inget Loggat'
      ),
    }));
  }, [act, selectedDate]);

  // Categories (for activity dimension only)
  const allCategories = useMemo(() => {
    const cats = new Set<string>();
    filteredAct.forEach((day) =>
      (day.activities || []).forEach((a: any) => {
        if (a.category) cats.add(a.category);
      })
    );
    return Array.from(cats).sort();
  }, [filteredAct]);

  // Build rows for activity dimension
  const activityTableData = useMemo(() => {
    if (dimension !== 'activities') return [];
    const rows: any[] = [];
    const seen = new Set<number>();

    for (const day of filteredAct) {
      for (let i = 0; i < (day.activities || []).length; i++) {
        const a = day.activities[i];
        if (seen.has(a.taxonomy)) continue;
        seen.add(a.taxonomy);

        // Prefer the activity name as it was logged in the data (this is what the
        // graph displays). Only fall back to a taxonomy lookup if the logged name
        // is missing, otherwise we can show a name that belongs to a different
        // activity when taxonomy IDs differ between category sets.
        const taxObj = taxonomyObjects.find((t: any) => t.taxonomy === a.taxonomy);
        const activityName =
          a.activity && a.activity !== 'Inget Loggat'
            ? a.activity
            : taxObj?.name || `Aktivitet ${a.taxonomy}`;

        rows.push({
          name: activityName,
          taxonomy: a.taxonomy,
          category: a.category,
          count: calcTotal(filteredAct, a.taxonomy),
          countPct: calcTotalPerc(filteredAct, a.taxonomy),
          totalMins: calcTotalTime(filteredAct, a.taxonomy),
          timePct: calcTimePercentage(filteredAct, a.taxonomy),
          minTime: calcTimeMin(filteredAct, a.taxonomy),
          avgMins: calcTimeAvarage(filteredAct, a.taxonomy),
          days: calcNumberOfDays(filteredAct, a.taxonomy),
          daysPct: calcDaysPercentage(filteredAct, a.taxonomy),
        });
      }
    }

    let filtered = rows;
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((r) => r.category === selectedCategory);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.category || '').toLowerCase().includes(q) ||
          String(r.taxonomy).includes(q)
      );
    }
    return filtered;
  }, [filteredAct, selectedCategory, searchText, taxonomyObjects, dimension]);

  // Build rows for non-activity dimensions
  const dimensionTableData = useMemo(() => {
    if (dimension === 'activities') return [];
    let rows = buildDimensionRows(filteredAct, dimension, lang);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }
    return rows;
  }, [filteredAct, dimension, lang, searchText]);

  const tableData = dimension === 'activities' ? activityTableData : dimensionTableData;

  // Category time summary (activities only)
  const summaryData = useMemo(() => {
    if (dimension !== 'activities') return [];
    const summary: any[] = [];
    for (const cat of allCategories) {
      const pct = calcTotalTimeCategory(filteredAct, cat);
      summary.push({ category: cat, timePercentage: pct });
    }
    summary.push({
      category: lang === 'EN' ? 'Total activities' : 'Totalt aktiviteter',
      timePercentage: calTotalNumberOfActivites(filteredAct),
    });
    return summary;
  }, [filteredAct, allCategories, lang, dimension]);

  // Columns per dimension
  const activityColumns = [
    { name: lang === 'EN' ? 'Activity' : 'Aktivitet', selector: (r: any) => r.name, sortable: true, grow: 2 },
    { name: lang === 'EN' ? 'Code' : 'Kod', selector: (r: any) => r.taxonomy, sortable: true, width: '80px' },
    { name: lang === 'EN' ? 'Category' : 'Kategori', selector: (r: any) => r.category, sortable: true },
    { name: lang === 'EN' ? 'Frequency' : 'Frekvens', selector: (r: any) => r.count, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Freq %' : 'Frekv %', selector: (r: any) => r.countPct, sortable: true, width: '80px' },
    { name: lang === 'EN' ? 'Total min' : 'Total min', selector: (r: any) => r.totalMins, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Time %' : 'Tid %', selector: (r: any) => r.timePct, sortable: true, width: '80px' },
    { name: lang === 'EN' ? 'Min time' : 'Min tid', selector: (r: any) => r.minTime, sortable: true, width: '80px' },
    { name: lang === 'EN' ? 'Avg time' : 'Snitt tid', selector: (r: any) => r.avgMins, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Days' : 'Dagar', selector: (r: any) => r.days, sortable: true, width: '70px' },
    { name: lang === 'EN' ? 'Days %' : 'Dagar %', selector: (r: any) => r.daysPct, sortable: true, width: '80px' },
  ];

  const dimensionColumns = [
    { name: DIMENSION_LABELS[dimension]?.[lang] || dimension, selector: (r: any) => r.name, sortable: true, grow: 2 },
    { name: lang === 'EN' ? 'Frequency' : 'Frekvens', selector: (r: any) => r.count, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Freq %' : 'Frekv %', selector: (r: any) => r.countPct, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Total min' : 'Total min', selector: (r: any) => r.totalMins, sortable: true, width: '100px' },
    { name: lang === 'EN' ? 'Time %' : 'Tid %', selector: (r: any) => r.timePct, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Avg min' : 'Snitt min', selector: (r: any) => r.avgMins, sortable: true, width: '90px' },
    { name: lang === 'EN' ? 'Days' : 'Dagar', selector: (r: any) => r.days, sortable: true, width: '80px' },
    { name: lang === 'EN' ? 'Days %' : 'Dagar %', selector: (r: any) => r.daysPct, sortable: true, width: '90px' },
  ];

  const summaryColumns = [
    { name: lang === 'EN' ? 'Category' : 'Kategori', selector: (r: any) => r.category, sortable: true, grow: 2 },
    { name: lang === 'EN' ? 'Time %' : 'Tid %', selector: (r: any) => r.timePercentage, sortable: true },
  ];

  const customStyles = {
    headCells: {
      style: { fontSize: '12px', fontWeight: 700, paddingLeft: '8px', paddingRight: '8px' },
    },
    cells: {
      style: { fontSize: '13px', paddingLeft: '8px', paddingRight: '8px' },
    },
  };

  const columns = dimension === 'activities' ? activityColumns : dimensionColumns;

  // Excel export
  const exportExcel = useCallback(() => {
    const wb = XLSX.utils.book_new();

    // Main data sheet
    const wsData = tableData.map((r: any) => {
      if (dimension === 'activities') {
        return {
          [lang === 'EN' ? 'Activity' : 'Aktivitet']: r.name,
          [lang === 'EN' ? 'Code' : 'Kod']: r.taxonomy,
          [lang === 'EN' ? 'Category' : 'Kategori']: r.category,
          [lang === 'EN' ? 'Frequency' : 'Frekvens']: r.count,
          [lang === 'EN' ? 'Freq %' : 'Frekv %']: r.countPct,
          [lang === 'EN' ? 'Total min' : 'Total min']: r.totalMins,
          [lang === 'EN' ? 'Time %' : 'Tid %']: r.timePct,
          [lang === 'EN' ? 'Min time' : 'Min tid']: r.minTime,
          [lang === 'EN' ? 'Avg time' : 'Snitt tid']: r.avgMins,
          [lang === 'EN' ? 'Days' : 'Dagar']: r.days,
          [lang === 'EN' ? 'Days %' : 'Dagar %']: r.daysPct,
        };
      }
      return {
        [DIMENSION_LABELS[dimension]?.[lang] || dimension]: r.name,
        [lang === 'EN' ? 'Frequency' : 'Frekvens']: r.count,
        [lang === 'EN' ? 'Freq %' : 'Frekv %']: r.countPct,
        [lang === 'EN' ? 'Total min' : 'Total min']: r.totalMins,
        [lang === 'EN' ? 'Time %' : 'Tid %']: r.timePct,
        [lang === 'EN' ? 'Avg min' : 'Snitt min']: r.avgMins,
        [lang === 'EN' ? 'Days' : 'Dagar']: r.days,
        [lang === 'EN' ? 'Days %' : 'Dagar %']: r.daysPct,
      };
    });
    const ws = XLSX.utils.json_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, DIMENSION_LABELS[dimension]?.[lang] || dimension);

    // Summary sheet (activities only)
    if (dimension === 'activities' && summaryData.length > 0) {
      const wsSummary = XLSX.utils.json_to_sheet(
        summaryData.map((r: any) => ({
          [lang === 'EN' ? 'Category' : 'Kategori']: r.category,
          [lang === 'EN' ? 'Time %' : 'Tid %']: r.timePercentage,
        }))
      );
      XLSX.utils.book_append_sheet(wb, wsSummary, lang === 'EN' ? 'Summary' : 'Sammanfattning');
    }

    XLSX.writeFile(wb, `Vardagen_HVB_${DIMENSION_LABELS[dimension]?.EN || dimension}.xlsx`);
  }, [tableData, summaryData, dimension, lang]);

  return (
    <div className="w-full">
      {/* Dimension tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1">
        {(Object.keys(DIMENSION_LABELS) as Dimension[]).map((dim) => (
          <button
            key={dim}
            onClick={() => { setDimension(dim); setSelectedCategory('all'); setSearchText(''); }}
            className={`flex-1 text-xs font-semibold py-2 rounded-md transition-all duration-150 ${
              dimension === dim
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={dimension === dim ? { borderBottom: `2px solid ${DIMENSION_COLORS[dim]}` } : {}}
          >
            {DIMENSION_LABELS[dim][lang]}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-4 bg-white border border-gray-200 rounded-lg p-4 mb-4">
        {/* Date filter */}
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-500 mb-1">
            {lang === 'EN' ? 'Date' : 'Datum'}
          </label>
          <select
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            <option value="all">{lang === 'EN' ? 'All dates' : 'Alla datum'}</option>
            {act.map((day, i) => (
              <option key={i} value={String(i)}>
                {day.name} – {day.date}
              </option>
            ))}
          </select>
        </div>

        {/* Category filter (activities only) */}
        {dimension === 'activities' && (
          <div className="flex flex-col">
            <label className="text-xs font-semibold text-gray-500 mb-1">
              {lang === 'EN' ? 'Category' : 'Kategori'}
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
            >
              <option value="all">{lang === 'EN' ? 'All categories' : 'Alla kategorier'}</option>
              {allCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Search */}
        <div className="flex flex-col flex-grow">
          <label className="text-xs font-semibold text-gray-500 mb-1">
            {lang === 'EN' ? 'Search' : 'Sök'}
          </label>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={lang === 'EN' ? 'Search...' : 'Sök...'}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        {/* Excel export */}
        <div className="flex flex-col justify-end">
          <button
            onClick={exportExcel}
            className="bg-emerald-600 text-white text-sm py-1.5 px-4 rounded font-semibold hover:bg-emerald-700 transition-colors flex items-center gap-1.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Excel
          </button>
        </div>
      </div>

      {/* Results count */}
      <div className="text-xs text-gray-500 mb-2">
        {tableData.length} {lang === 'EN' ? 'rows' : 'rader'}
        {dimension === 'activities' && selectedCategory !== 'all' && ` · ${selectedCategory}`}
        {searchText && ` · "${searchText}"`}
      </div>

      {/* Data table */}
      <div className="bg-white rounded-lg border border-gray-200 mb-4">
        <DataTable
          columns={columns}
          data={tableData}
          pagination
          paginationPerPage={15}
          paginationRowsPerPageOptions={[10, 15, 25, 50]}
          dense
          customStyles={customStyles}
          noDataComponent={
            <div className="py-8 text-gray-400 text-sm">
              {lang === 'EN' ? 'No data found' : 'Ingen data hittades'}
            </div>
          }
        />
      </div>

      {/* Category time summary (activities only) */}
      {dimension === 'activities' && summaryData.length > 0 && (
        <>
          <h3 className="text-sm font-bold text-gray-700 mb-2">
            {lang === 'EN' ? 'Time per category (%)' : 'Tid per kategori (%)'}
          </h3>
          <div className="bg-white rounded-lg border border-gray-200">
            <DataTable
              columns={summaryColumns}
              data={summaryData}
              dense
              customStyles={customStyles}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default HVBStatistics;
